import React, { Children, cloneElement, KeyboardEvent, ReactElement } from 'react';

const isAnchorTag = (element: HTMLElement): boolean => element.tagName.toLowerCase() === 'a';

const hasButtonRole = (element: HTMLElement): boolean => element.getAttribute('role') === 'button';

const shouldClickOnSpace = (element: HTMLElement): boolean => {
    // Don't click <a>s on space-bar-press *unless* it has role="button"
    // This is what screenreaders expect; see http://www.last-child.com/keyboard-accessibility-with-the-space-bar/
    return !isAnchorTag(element) || hasButtonRole(element);
};

// When a user tabs to the element and hits `Enter` or `Space`✫, then run the action
//
// ✫ `<a>` links will only accept `Enter` unless they have `role="button".`
export const runActionableKeys = (
    event: KeyboardEvent<HTMLElement>,
    action: (event: KeyboardEvent<HTMLElement>) => void,
): void => {
    if (
        event.key === 'Enter' ||
        (event.key === ' ' && shouldClickOnSpace(event.currentTarget))
    ) {
        action(event);
    }
};

interface KeyboardClickableProps {
    children: ReactElement;
}

/**
 * Wrap children in this element to make them respond to the enter key and
 * space bar as if they were being clicked. Note that you still need to set
 * tabIndex on the child elements.
 *
 * See shouldClickOnSpace for more information on differences in how
 * the enter key and space bar should be handled.
 */
export const KeyboardClickable: React.FC<KeyboardClickableProps> = ({ children }) => {
    const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        runActionableKeys(event, (event) => {
            // Originally, we did this:
            // event.currentTarget.click();
            // That won't work in SVG elements, which don't have a click() method. But this works:
            const clickEvent = new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window,
            });
            (event.currentTarget as HTMLElement).dispatchEvent(clickEvent);
            // See https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent
        });
    };

    const wrappedChildren = Children.map(children, (child) => {
        return cloneElement(child, { onKeyDown });
    });

    return <span>{wrappedChildren}</span>;
};
